import { useTranslation } from "react-i18next";

/**
 * Privacy Policy, version 3.3 (effective 13 September 2026).
 * Canonical text shared by the public legal page and the authenticated portal.
 * Section ids match the anchors the apps link to.
 */
export function PrivacyContent() {
  const { t } = useTranslation("common");
  return (
    <>
      <p className="text-sm text-muted-foreground mb-4">{t("web.legal.englishOnly")}</p>
      <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-1">
        Provided by Elefa Mobile Pty Ltd, ABN 16 694 346 088, Melbourne, Australia.
      </p>
      <p className="text-sm text-muted-foreground mb-8">Version 3.3 · Effective 13 September 2026</p>

      <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
        <section>
          <p className="text-muted-foreground">
            <strong>In short.</strong> We collect what the service needs: your number, your email,
            your call and payment records, your device details. We use them to run the service, bill
            you, keep you safe and answer you. We do not sell your information. Contacts you pick
            stay on your phone. You can see, correct or delete what we hold.
          </p>
        </section>

        <section>
          <h2 id="who" className="text-2xl font-semibold mb-4 scroll-mt-24">1. Who we are</h2>
          <p className="text-muted-foreground">
            <strong>Elefa Mobile Pty Ltd</strong> provides elefa Connect and is bound by the
            Australian Privacy Principles in the Privacy Act 1988. Our Privacy Officer:{" "}
            <a href="mailto:privacy@elefa.com.au" className="text-primary hover:underline">privacy@elefa.com.au</a>.
          </p>
        </section>

        <section>
          <h2 id="collect" className="text-2xl font-semibold mb-4 scroll-mt-24">2. What we collect</h2>
          <div className="text-muted-foreground space-y-3">
            <p><strong>Account:</strong> your mobile number, your email address, your display name and language.</p>
            <p>
              <strong>Payments:</strong> top-up amounts, currency, card type and last four digits,
              and the history of credit, refunds and chargebacks. Card numbers go directly to Stripe
              and never touch our systems.
            </p>
            <p>
              <strong>Calls:</strong> the number called, your caller ID, start and end time,
              duration, route and cost. We do not record call audio.
            </p>
            <p><strong>Credit transfers:</strong> sender, recipient, amount, time and the balances before and after.</p>
            <p>
              <strong>Contacts:</strong> when you use Contacts to pick a number, your phone's own
              picker opens and the number you pick is used for that action. When you match contacts
              to find friends on elefa, the app sends a one-way SHA-256 hash of each phone number,
              never the number itself and never the name. Your contact list is not stored on our
              servers.
            </p>
            <p>
              <strong>Device and network:</strong> device model, operating system, app version, IP
              address, approximate location from the IP, network type, and push notification tokens.
            </p>
            <p><strong>Diagnostics:</strong> crash reports and call quality measurements.</p>
            <p>
              <strong>Identity:</strong> where the law or our security rules require verification for
              higher limits, the identity information you provide and the result.
            </p>
            <p>
              <strong>Website:</strong> the site uses cookies for sign-in and basic analytics; you
              can control non-essential cookies in the banner.
            </p>
          </div>
        </section>

        <section>
          <h2 id="why" className="text-2xl font-semibold mb-4 scroll-mt-24">3. Why</h2>
          <p className="text-muted-foreground">
            To run your account and connect your calls; to bill you and process payments and refunds;
            to send service messages; to send updates unless you opt out; to detect and prevent
            fraud, including patterns across accounts; to support you and handle complaints; to
            improve the service; and to meet legal obligations.
          </p>
        </section>

        <section>
          <h2 id="share" className="text-2xl font-semibold mb-4 scroll-mt-24">4. Who receives it</h2>
          <p className="text-muted-foreground">
            <strong>We do not sell your information.</strong> It goes to those who need it to provide
            the service:
          </p>
          <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
            <li>Interconnect carriers: the number called, your caller ID, and call timing, to connect the call.</li>
            <li>Stripe, for payments and disputes.</li>
            <li>Twilio, for the SMS and WhatsApp codes that verify your number and for invitations you send.</li>
            <li>Resend, for email.</li>
            <li>Firebase, for crash reports and push notifications; Apple and Google, for push delivery.</li>
            <li>Supabase, our database and authentication provider, hosted in AWS eu-north-1, Stockholm, Sweden.</li>
            <li>Identity verification providers, where verification applies.</li>
            <li>Professional advisers under confidentiality; courts, regulators and law enforcement where the law compels or permits it; a buyer or successor in a corporate transaction, under this policy.</li>
          </ul>
          <p className="text-muted-foreground mt-4">
            Some of these providers operate outside Australia. We take the steps the Australian
            Privacy Principles require before information leaves Australia.
          </p>
        </section>

        <section>
          <h2 id="security" className="text-2xl font-semibold mb-4 scroll-mt-24">5. Security</h2>
          <p className="text-muted-foreground">
            <strong>Encryption in transit</strong> between your device and our network, encryption at
            rest, access limited to staff who need it, multi-factor authentication for staff, logging
            and monitoring, and a tested incident response. If a breach is likely to cause serious
            harm we notify you and the Commissioner under the Notifiable Data Breaches scheme.
          </p>
        </section>

        <section>
          <h2 id="retention" className="text-2xl font-semibold mb-4 scroll-mt-24">6. How long</h2>
          <p className="text-muted-foreground">
            Account information while your account is open and 12 months after; call records 2 years;
            payment and credit transfer records 7 years; identity verification records 7 years after
            the relationship ends, where collected; support correspondence 3 years; security logs 24
            months. After that, deleted or de-identified.
          </p>
        </section>

        <section>
          <h2 id="rights" className="text-2xl font-semibold mb-4 scroll-mt-24">7. Your rights</h2>
          <p className="text-muted-foreground">
            <strong>You can</strong> access the information we hold about you, correct it, opt out of
            updates, close your account and ask for deletion of what we are not required to keep, and
            complain. Write to{" "}
            <a href="mailto:privacy@elefa.com.au" className="text-primary hover:underline">privacy@elefa.com.au</a>;
            we respond within 30 days. If you are not satisfied you can complain to the Office of the
            Australian Information Commissioner, oaic.gov.au, 1300 363 992.
          </p>
        </section>

        <section>
          <h2 id="requests" className="text-2xl font-semibold mb-4 scroll-mt-24">8. Government requests</h2>
          <p className="text-muted-foreground">
            We require valid legal process before disclosing account information, except in an
            emergency involving imminent risk to life, as the law permits.
          </p>
        </section>

        <section>
          <h2 id="children" className="text-2xl font-semibold mb-4 scroll-mt-24">9. Children</h2>
          <p className="text-muted-foreground">
            The service is for people 18 and over. We do not knowingly collect information from
            anyone younger; tell us and we will delete it.
          </p>
        </section>

        <section>
          <h2 id="changes" className="text-2xl font-semibold mb-4 scroll-mt-24">10. Changes</h2>
          <p className="text-muted-foreground">
            Material changes are notified before they take effect. The version and date at the top
            are current.
          </p>
        </section>

        <section>
          <h2 id="contact" className="text-2xl font-semibold mb-4 scroll-mt-24">11. Contact</h2>
          <p className="text-muted-foreground">
            Privacy Officer, Elefa Mobile Pty Ltd, Melbourne, Australia.{" "}
            <a href="mailto:privacy@elefa.com.au" className="text-primary hover:underline">privacy@elefa.com.au</a>.
          </p>
        </section>
      </div>
    </>
  );
}
